<template>
  <div
    class="event-selector"
    :class="{ 'with-show-selector': useShowSelector, 'with-labels': useLabels }"
  >
    <div class="grid">
      <template v-if="useLabels">
        <label>Evenemang</label>
        <label v-if="useShowSelector">Föreställning</label>
      </template>

      <EventSelectorEventSelect
        v-model="selectedEventInner"
        :events="allEvents"
        :placeholder="eventSelectorPlaceholder"
      />

      <EventSelectorShowSelect
        v-if="useShowSelector"
        v-model="selectedShowInner"
        :is-loaded="!areShowsLoading"
        :shows="availableShows"
        :is-disabled="!selectedEventInner"
        :placeholder="showSelectorPlaceholder"
      />
    </div>
    <div
      v-if="shouldShowPlaceholderComponent"
      class="module-info-container"
    >
      <component :is="placeholderComponent" />
    </div>
  </div>
</template>

<script>
import remoteEventProvider from '@/components/common/input/event-selector-v3/data-provider/remoteEventProvider';
import EventSelectorShowSelect from '@/components/common/input/event-selector-v3/EventSelectorShowSelect.vue';
import EventSelectorEventSelect from '@/components/common/input/event-selector-v3/EventSelectorEventSelect.vue';

export default {
  name: 'EventSelectorV3',
  components: {
    EventSelectorEventSelect,
    EventSelectorShowSelect,
  },
  props: {
    useShowSelector: {
      type: Boolean,
      default: false,
    },
    useLabels: {
      type: Boolean,
      default: true,
    },
    selectedEvent: {
      type: Object,
      default: null,
    },
    selectedEventId: {
      type: Number,
      default: null,
    },
    selectedEventIndex: {
      type: [Number, String],
      default: null,
    },
    selectedShow: {
      type: Object,
      default: null,
    },
    selectedShowId: {
      type: Number,
      default: null,
    },
    selectedShowIndex: {
      type: Number,
      default: -1,
    },
    eventProvider: {
      type: Object,
      default: () => remoteEventProvider,
    },
    showMapper: {
      type: Function,
      default: (showData = []) => showData.map((show, index) => ({
        ...show,
        key: index,
      })),
    },
    eventSelectorPlaceholder: {
      type: String,
      default: 'Välj evenemang',
    },
    showSelectorPlaceholder: {
      type: String,
      default: 'Välj föreställning',
    },
    placeholderComponent: {
      type: [Object],
      default: null,
    },
  },
  data() {
    return {
      selectedEventInner: null,
      selectedShowInner: null,
      availableShows: [],
      areShowsLoading: false,
      initialEventFilter: {
        text: '',
        onlyActive: true,
      },
      allEvents: [],
    };
  },
  computed: {
    shouldShowPlaceholderComponent() {
      return this.placeholderComponent
        && ((this.useShowSelector && !this.selectedShowInner) || !this.selectedEventInner);
    },
  },
  watch: {
    useShowSelector(newValue) {
      if (this.useShowSelector && newValue) {
        this.loadShowsForSelectedEvent(this.selectedEventInner);
      }
    },
    selectedEvent(event) {
      this.selectedEventInner = event
        ? this.allEvents.find((matchEvent) => matchEvent.id === event.id) || null
        : null;
      this.selectedShowInner = null;
    },
    selectedEventId(eventId) {
      this.selectedEventInner = eventId
        ? this.allEvents.find((event) => event.id === eventId) || null
        : null;
      this.selectedShowInner = null;
    },
    selectedEventIndex(eventIndex) {
      this.selectedEventInner = eventIndex
        ? this.allEvents[eventIndex] || null
        : null;
      this.selectedShowInner = null;
    },
    selectedShow(show) {
      if (this.useShowSelector && !this.areShowsLoading) {
        this.selectedShowInner = show
          ? this.availableShows.find((matchShow) => matchShow.id === show.id) || null
          : null;
      }
    },
    selectedShowId(showId) {
      if (this.useShowSelector && !this.areShowsLoading) {
        this.selectedShowInner = showId
          ? this.availableShows.find((show) => show.id === showId) || null
          : null;
      }
    },
    selectedShowIndex(showIndex) {
      if (this.useShowSelector && !this.areShowsLoading) {
        this.selectedShowInner = showIndex >= 0
          ? this.availableShows[showIndex] || null
          : null;
      }
    },
    selectedEventInner(newValue) {
      this.loadShowsForSelectedEvent(newValue);

      this.updateSelectedEvent(newValue);
    },
    selectedShowInner(newValue) {
      if (this.useShowSelector) {
        this.updateSelectedShow(newValue);
      }
    },
  },
  async mounted() {
    this.allEvents = await this.eventProvider.getEvents();

    if (this.selectedEvent) {
      this.selectedEventInner = this.allEvents.find((event) => event.id === this.selectedEvent.id) || null;
    } else if (this.selectedEventId) {
      this.selectedEventInner = this.allEvents.find((event) => event.id === this.selectedEventId) || null;
    } else if (this.selectedEventIndex) {
      this.selectedEventInner = this.allEvents[this.selectedEventIndex] || null;
    } else {
      this.selectedEventInner = null;
    }
  },
  methods: {
    loadShowsForSelectedEvent(event) {
      this.toggleAreShowsLoading();

      if (event) {
        this.eventProvider.getShowsForEvent(event.id, this.showMapper)
          .then(this.setShows)
          .then(this.selectShow)
          .catch(this.resetShows)
          .finally(this.toggleAreShowsLoading);
      } else {
        this.availableShows = [];
        this.toggleAreShowsLoading();
      }
    },
    selectShow() {
      if (this.selectedShow) {
        this.selectedShowInner = this.availableShows.find((show) => show.id === this.selectedShow.id) || null;
      } else if (this.selectedShowIndex >= 0) {
        this.selectedShowInner = this.availableShows[this.selectedShowIndex] || null;
      } else if (this.selectedShowId) {
        this.selectedShowInner = this.availableShows.find((show) => show.id === this.selectedShowId) || null;
      } else {
        this.selectedShowInner = this.availableShows.length === 1
          ? this.availableShows[0]
          : null;
      }
    },
    setShows(shows) {
      this.availableShows = shows;
      this.$emit('shows-loaded', shows);
    },
    resetShows() {
      this.availableShows = [];
    },
    toggleAreShowsLoading() {
      this.areShowsLoading = !this.areShowsLoading;
    },
    updateSelectedEvent(selectedEvent) {
      const selectedEventId = selectedEvent
        ? selectedEvent.id
        : null;
      const selectedEventIndex = selectedEvent
        ? this.allEvents.findIndex((event) => event.id === selectedEvent.id)
        : -1;

      this.$emit('update:selected-event', selectedEvent);
      this.$emit('update:selected-event-id', selectedEventId);
      this.$emit('update:selected-event-index', selectedEventIndex);
    },
    updateSelectedShow(selectedShow) {
      const selectedShowId = selectedShow
        ? selectedShow.id
        : null;
      const selectedShowIndex = selectedShow
        ? selectedShow.key
        : null;

      this.$emit('update:selected-show', selectedShow);
      this.$emit('update:selected-show-id', selectedShowId);
      this.$emit('update:selected-show-index', selectedShowIndex);
    },
  },
};
</script>

<style lang="scss" scoped>
  .event-selector {
    &.with-labels {
      .grid {
        grid-template-rows: min-content max-content;
      }
    }

    &.with-show-selector {
      .grid {
        grid-template-columns: minmax(100px, 600px) minmax(100px, 600px);

        @media (max-width: $smallBreakpoint) {
          grid-template-columns: 1fr;
        }
      }
    }

    .grid {
      grid-template-rows: max-content;
      grid-template-columns: minmax(100px, 600px);
      display: grid;
      grid-row-gap: 2px;
      grid-column-gap: 10px;
    }

    .module-info-container {
      width: 100%;
      display: flex;
      justify-items: center;
    }
  }
</style>
